import {
  Breadcrumbs,
  Card,
  CardsGrid,
  PageContent,
  PageHeader,
  SearchBar,
  Spinner,
} from '@campx-org/shared'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Alert, Box, Chip, Stack, Typography } from '@mui/material'
import { NoDataComponent } from 'components/NoDataComponent'
import { useState } from 'react'
import { useQuery } from 'react-query'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { EXAMS } from 'services'
import { PaginationFooter } from './PaginationFooter'
import { DEFAULT_PAGE_SIZE } from './utils'

type SubjectRow = {
  date?: string
  fromTimeString?: string
  toTimeString?: string
  subjectCode?: string
  subjectName?: string
  sections?: string[]
  bundleCount?: number
  evaluationId?: string
  groupId?: string
}

type BreadcrumbLink = { name: string; to: string | null }

interface SubjectItem {
  key: string
  title: string
  meta?: string
  date?: string
  timeRange?: string
  bundleCount: number
  raw: SubjectRow
}

interface SubjectListRendererProps {
  subjects: SubjectItem[]
  totalCount: number
  totalPages: number
  page: number
  limit: number
  search: string
  isLoading: boolean
  isError: boolean
  breadcrumbs: BreadcrumbLink[]
  onSelect: (item: SubjectItem) => void
  onSearch: (val: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onRefetch: () => void
}

function SubjectListRenderer({
  subjects,
  totalCount,
  totalPages,
  page,
  limit,
  search,
  isLoading,
  isError,
  breadcrumbs,
  onSelect,
  onSearch,
  onPageChange,
  onLimitChange,
  onRefetch,
}: SubjectListRendererProps) {
  return (
    <>
      <PageHeader title={<Breadcrumbs links={breadcrumbs} />} />

      <PageContent>
        <Stack spacing={2}>
          {isError && (
            <Alert severity="error" onClose={() => onRefetch()}>
              Failed to load subjects.
            </Alert>
          )}

          <Box sx={{ maxWidth: 400 }}>
            <SearchBar
              placeholder="Search subjects..."
              onSearch={onSearch}
              textFieldProps={{ size: 'small' }}
            />
          </Box>

          {isLoading ? (
            <Spinner />
          ) : subjects.length ? (
            <>
              <CardsGrid cardWidth={320} gap={16}>
                {subjects.map((subject) => (
                  <Card
                    key={subject.key}
                    title={subject.title}
                    footer={{
                      buttonText: 'View Bundles',
                      onClick: () => onSelect(subject),
                      endIcon: <ChevronRightIcon />,
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {subject.meta && (
                          <Chip
                            label={subject.meta}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                        {subject.timeRange && (
                          <Typography color="text.secondary" variant="body2">
                            {subject.timeRange}
                          </Typography>
                        )}
                      </Stack>
                      {subject.date && (
                        <Typography color="text.secondary" variant="body2">
                          Date: {subject.date}
                        </Typography>
                      )}
                      <Typography color="text.secondary" variant="body2">
                        Bundles: {subject.bundleCount}
                      </Typography>
                    </Stack>
                  </Card>
                ))}
              </CardsGrid>

              <PaginationFooter
                page={page}
                limit={limit}
                totalCount={totalCount}
                onPageChange={onPageChange}
                onLimitChange={onLimitChange}
              />
            </>
          ) : (
            <NoDataComponent
              message={
                search
                  ? 'No subjects found matching filters.'
                  : 'No subjects found.'
              }
            />
          )}
        </Stack>
      </PageContent>
    </>
  )
}

export default function AdminSubjectListPage() {
  const params = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  const examGroupId = Number(params.examGroupId)
  const examGroup = location.state?.examGroup

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)

  const handleSearch = (val: string) => {
    setSearch(val === '' ? '' : val)
    setPage(1)
  }

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit)
    setPage(1)
  }

  const offset = (page - 1) * limit
  const dependencies = [examGroupId, 'external', search, limit, offset]

  const { data, isLoading, isError, refetch } = useQuery(
    ['admin-subjects', ...dependencies],
    () =>
      EXAMS.adminBundleEvaluation.getExamSubjects(examGroupId, {
        examCategory: 'external',
        search: search || undefined,
        limit,
        offset,
      }),
    { enabled: !!examGroupId },
  )

  const rawSubjects: SubjectRow[] = data?.subjects || []
  const totalCount = data?.count ?? 0
  const totalPages = Math.ceil(totalCount / limit)

  const subjects: SubjectItem[] = rawSubjects.map((s) => ({
    key: `${s.subjectCode}-${s.date}-${s.fromTimeString}-${s.toTimeString}`,
    title: s.subjectName || '-',
    meta: s.subjectCode || undefined,
    date: s.date || undefined,
    timeRange: s.fromTimeString
      ? `${s.fromTimeString}-${s.toTimeString}`
      : undefined,
    bundleCount: s.bundleCount || 0,
    raw: s,
  }))

  const handleSelect = (item: SubjectItem) => {
    const s = item.raw
    const sp = new URLSearchParams()
    sp.set('subjectName', s.subjectName || '')
    sp.set('subjectCode', s.subjectCode || '')
    sp.set('date', s.date || '')
    sp.set('fromTimeString', s.fromTimeString || '')
    sp.set('toTimeString', s.toTimeString || '')
    navigate(
      `/admin/examinations/${examGroupId}/subjects/bundles?${sp.toString()}`,
    )
  }

  const breadcrumbs: BreadcrumbLink[] = [
    { name: 'Exams', to: '/admin/examinations' },
    {
      name: data?.groupName || examGroup?.groupName || 'Examination',
      to: null,
    },
  ]

  return (
    <SubjectListRenderer
      subjects={subjects}
      totalCount={totalCount}
      totalPages={totalPages}
      page={page}
      limit={limit}
      search={search}
      isLoading={isLoading}
      isError={isError}
      breadcrumbs={breadcrumbs}
      onSelect={handleSelect}
      onSearch={handleSearch}
      onPageChange={setPage}
      onLimitChange={handleLimitChange}
      onRefetch={refetch}
    />
  )
}

export function AdminInternalSubjectListPage() {
  const params = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  const examGroupId = Number(params.examGroupId)
  const examGroup = location.state?.examGroup

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)

  const handleSearch = (val: string) => {
    setSearch(val === '' ? '' : val)
    setPage(1)
  }

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit)
    setPage(1)
  }

  const offset = (page - 1) * limit
  const dependencies = [examGroupId, 'internal', search, limit, offset]

  const { data, isLoading, isError, refetch } = useQuery(
    ['admin-subjects', ...dependencies],
    () =>
      EXAMS.adminBundleEvaluation.getExamSubjects(examGroupId, {
        examCategory: 'internal',
        search: search || undefined,
        limit,
        offset,
      }),
    { enabled: !!examGroupId },
  )

  const rawSubjects: SubjectRow[] = data?.subjects || []
  const totalCount = data?.count ?? 0
  const totalPages = Math.ceil(totalCount / limit)

  const subjects: SubjectItem[] = rawSubjects.map((s) => ({
    key: s.groupId || s.evaluationId || s.subjectCode || '',
    title: s.subjectName || '-',
    bundleCount: s.bundleCount || 0,
    raw: s,
  }))

  const handleSelect = (item: SubjectItem) => {
    const s = item.raw
    const sp = new URLSearchParams()
    sp.set('subjectName', s.subjectName || '')
    if (s.evaluationId) sp.set('evaluationId', s.evaluationId)
    if (s.groupId) sp.set('groupId', s.groupId)
    navigate(
      `/admin/internal/${examGroupId}/subjects/bundles?${sp.toString()}`,
      { state: { examGroup, examCategory: 'internal' } },
    )
  }

  const breadcrumbs: BreadcrumbLink[] = [
    { name: 'Exams', to: '/admin/examinations' },
    {
      name: data?.groupName || examGroup?.groupName || 'Examination',
      to: null,
    },
  ]

  return (
    <SubjectListRenderer
      subjects={subjects}
      totalCount={totalCount}
      totalPages={totalPages}
      page={page}
      limit={limit}
      search={search}
      isLoading={isLoading}
      isError={isError}
      breadcrumbs={breadcrumbs}
      onSelect={handleSelect}
      onSearch={handleSearch}
      onPageChange={setPage}
      onLimitChange={handleLimitChange}
      onRefetch={refetch}
    />
  )
}
